import { Fragment, useEffect, useState } from "react";
import { head } from "../components/common/PageHeader.jsx";
import { exportButton } from "../components/common/ExportButton.jsx";
import { orderTable } from "../components/orders/OrderTable.jsx";
import { orderDateFilters } from "../components/orders/orderFilters.jsx";

const ORDERS_API = "https://api.praachinorganics.com/api/admin/orders";

export default function OrdersPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  async function fetchOrders({
    start = startDate,
    end = endDate,
    currentPage = page,
    size = pageSize,
  } = {}) {
    const params = new URLSearchParams({
      page: String(currentPage),
      pageSize: String(size),
    });

    if (start) params.set("startDate", start);
    if (end) params.set("endDate", end);

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${ORDERS_API}?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Unable to load orders (${response.status}).`);
      }

      const result = await response.json();

      setRows(result.items ?? []);
      setTotalCount(result.totalCount ?? 0);
    } catch (err) {
      setRows([]);
      setTotalCount(0);
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchOrders({
      start: "",
      end: "",
      currentPage: 1,
      size: 10,
    });
  }, []);

  function applyFilters() {
    if (startDate && endDate && startDate > endDate) {
      setError("Start date cannot be after end date.");
      return;
    }

    setPage(1);

    fetchOrders({
      start: startDate,
      end: endDate,
      currentPage: 1,
      size: pageSize,
    });
  }

  function resetFilters() {
    setStartDate("");
    setEndDate("");
    setPage(1);

    fetchOrders({
      start: "",
      end: "",
      currentPage: 1,
      size: pageSize,
    });
  }

  function changePageSize(event) {
    const size = Number(event.target.value);

    setPageSize(size);
    setPage(1);

    fetchOrders({
      start: startDate,
      end: endDate,
      currentPage: 1,
      size,
    });
  }

  function changePage(nextPage) {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    if (nextPage < 1 || nextPage > totalPages || loading) {
      return;
    }

    setPage(nextPage);

    fetchOrders({
      start: startDate,
      end: endDate,
      currentPage: nextPage,
      size: pageSize,
    });
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const start = totalCount === 0 ? 0 : (page - 1) * pageSize;

  const end = Math.min(page * pageSize, totalCount);

  // Display the first page, last page, and nearby pages.
  const pages = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  ).filter(
    (number) =>
      number === 1 ||
      number === totalPages ||
      (number >= page - 1 && number <= page + 1),
  );

  return (
    <>
      {head(
        "OPERATIONS",
        "Orders",
        "Payment and fulfilment are tracked separately.",
        exportButton("orders"),
      )}

      <div className="card">
        {orderDateFilters({
          startDate,
          endDate,
          onStartDateChange: setStartDate,
          onEndDateChange: setEndDate,
          onApply: applyFilters,
          onReset: resetFilters,
        })}

        {error && (
          <p className="error-message" role="alert">
            {error}
          </p>
        )}

        {loading ? (
          <p className="order-loading" role="status">
            Loading orders...
          </p>
        ) : (
          orderTable(rows)
        )}

        {/* Pagination using your existing CSS class names */}
        <div className="pagination" role="group" aria-label="Table pagination">
          <span role="status">
            Showing {start + 1}–{end} of {totalCount}
          </span>

          <label>
            Rows per page{" "}
            <select
              value={pageSize}
              onChange={changePageSize}
              aria-label="Rows per page"
              disabled={loading}
            >
              {[10, 25, 50].map((number) => (
                <option key={number} value={number}>
                  {number}
                </option>
              ))}
            </select>
          </label>

          <div className="page-buttons">
            <button
              type="button"
              onClick={() => changePage(page - 1)}
              disabled={page === 1 || loading}
            >
              Previous
            </button>

            {pages.map((number, index) => (
              <Fragment key={number}>
                {index > 0 && number > pages[index - 1] + 1 && <span>…</span>}

                <button
                  type="button"
                  aria-current={number === page ? "page" : undefined}
                  aria-label={`Page ${number}`}
                  disabled={number === page || loading}
                  onClick={() => changePage(number)}
                >
                  {number}
                </button>
              </Fragment>
            ))}

            <button
              type="button"
              onClick={() => changePage(page + 1)}
              disabled={page >= totalPages || loading}
            >
              Next
            </button>

            <span>
              Page {page} of {totalPages}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
